import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { ArrowUpRight } from "lucide-react";
import { scrollToForm, trackEvent } from "@/lib/tracking";

const FAQS = [
  { q: "Do you only run Google Ads?", a: "No. Rekisa helps businesses run paid ads across Google, Facebook, Instagram, LinkedIn, and remarketing channels. The right platform depends on your offer, audience, budget, and sales goal." },
  { q: "Do I need a landing page?", a: "In most cases, yes. Paid ads perform better when the landing page matches the ad message and gives the visitor one clear action to take." },
  { q: "Do you offer WhatsApp follow-up?", a: "Yes, but WhatsApp is only one option. Rekisa can help set up automated follow-up through WhatsApp, email, CRM workflows, SMS, or another structure that fits the client's sales process." },
  { q: "Who pays the advertising budget?", a: "The client pays the advertising budget directly to the advertising platform. Rekisa charges for strategy, setup, management, optimisation, tracking, and reporting." },
  { q: "Can you help if we already run ads?", a: "Yes. Rekisa can review existing campaigns, tracking, landing pages, audiences, budgets, and lead quality before recommending what needs to be fixed." },
  { q: "Do you guarantee sales?", a: "No. Sales depend on the offer, pricing, market demand, sales process, budget, and follow-up. Rekisa focuses on proper setup, tracking, testing, optimisation, and lead quality improvement." },
  { q: "What is remarketing?", a: "Remarketing means showing ads to people who have already interacted with your business, such as website visitors, previous leads, video viewers, or people who engaged with your social pages." },
];

export function FAQSection() {
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-24 sm:py-32 bg-zinc-50/60 border-y border-zinc-200"
    >
      <div className="mx-auto max-w-4xl px-5 sm:px-8">
        <div className="text-center mb-12">
          <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-mono mb-4">Frequently asked</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-[1.05] text-zinc-900">
            Questions, answered.
          </h2>
        </div>

        <Accordion
          type="single"
          collapsible
          className="space-y-3"
          onValueChange={(v) => v && trackEvent("faq_open", { item: v })}
        >
          {FAQS.map((item, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              data-testid={`faq-item-${i}`}
              className="rounded-2xl border border-zinc-200 bg-white px-5 sm:px-6 data-[state=open]:border-zinc-300 data-[state=open]:shadow-sm transition-all"
            >
              <AccordionTrigger
                data-testid={`faq-trigger-${i}`}
                className="font-display text-left text-lg sm:text-xl font-bold text-zinc-900 hover:no-underline py-5"
              >
                {item.q}
              </AccordionTrigger>
              <AccordionContent
                data-testid={`faq-content-${i}`}
                className="text-zinc-700 leading-relaxed text-[15px] pb-5"
              >
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

export function FinalCTASection() {
  const handleCta = () => {
    trackEvent("cta_click", { location: "final_cta" });
    scrollToForm();
  };

  return (
    <section
      data-testid="final-cta-section"
      className="relative py-24 sm:py-32 bg-white overflow-hidden"
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[420px] w-[820px] rounded-full opacity-30 blur-[120px]"
          style={{ background: "conic-gradient(from 0deg, #4285f4, #34a853, #fbbc04, #ea4335, #4285f4)" }} />
      </div>
      <div className="mx-auto max-w-5xl px-5 sm:px-8 text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3.5 py-1.5 text-[11px] tracking-[0.14em] uppercase text-zinc-700 font-semibold shadow-sm mb-6">
          <span className="h-1.5 w-1.5 rounded-full bg-[#1a73e8] pulse-dot" />
          Let's get you leads
        </div>
        <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-[-0.035em] leading-[0.98] text-zinc-900">
          Ready for more leads?
        </h2>
        <p className="mt-6 text-zinc-600 max-w-2xl mx-auto leading-relaxed text-lg">
          Tell us what you want to promote, when you want to go live, and what budget you have.
        </p>
        <div className="mt-9 flex justify-center">
          <Button
            onClick={handleCta}
            data-testid="final-cta-request-quote"
            className="bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full h-14 px-8 text-base font-semibold shadow-[0_12px_32px_rgba(26,115,232,0.4)] hover:shadow-[0_16px_40px_rgba(26,115,232,0.5)] transition-all"
          >
            Request a Quote
            <ArrowUpRight className="ml-1 h-5 w-5" />
          </Button>
        </div>
      </div>
    </section>
  );
}

const ANSWERS = [
  {
    q: "What is paid ads management?",
    a: "Paid ads management is the planning, setup, monitoring, and optimisation of advertising campaigns on platforms such as Google, Facebook, Instagram, and LinkedIn. The goal is to generate leads, enquiries, bookings, registrations, or sales at a controlled cost.",
    color: "#4285f4",
  },
  {
    q: "What is remarketing?",
    a: "Remarketing is a paid ads method that targets people who have already interacted with your business, such as website visitors, previous leads, video viewers, or social media engagers.",
    color: "#ea4335",
  },
  {
    q: "Which paid ads platform is best?",
    a: "It depends on the goal. Google Ads works well when people are already searching. Meta Ads works for awareness, offers, retargeting and visual campaigns. LinkedIn works for B2B.",
    color: "#fbbc04",
  },
  {
    q: "What does Rekisa include in paid ads management?",
    a: "Strategy, campaign setup, targeting, remarketing, tracking, reporting, and monthly optimisation across Google, Meta, and LinkedIn.",
    color: "#34a853",
  },
];

export function AEOAnswerBlocks() {
  return (
    <section
      data-testid="aeo-section"
      className="relative py-20 sm:py-24 bg-zinc-50/60 border-y border-zinc-200"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl mb-10">
          <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-mono mb-4">Quick answers</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-[-0.03em] leading-[1.05] text-zinc-900">
            Paid ads, briefly.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {ANSWERS.map((it) => (
            <div key={it.q} className="rounded-2xl border border-zinc-200 bg-white p-6 hover:shadow-sm transition-shadow">
              <span className="h-1 w-10 rounded-full block mb-4" style={{ background: it.color }} />
              <h3 className="font-display text-lg font-bold text-zinc-900 tracking-tight">{it.q}</h3>
              <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{it.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
